import { z } from 'zod'
import type { Pos } from '../grid'
import type { Color, ErrorCode, PlayerPublic } from '../protocol'
import { CLASS_IDS } from './content'
import type { ClassId, ItemId, MonsterId, PerkId, RunStatus } from './types'

export type DungeonKind = 'daily' | 'practice'

// ---- intents (what the client may say) --------------------------------------------------------

export const dungeonIntentSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('move'), dir: z.enum(['up', 'down', 'left', 'right']) }),
  z.object({ type: z.literal('wait') }),
  z.object({ type: z.literal('use'), slot: z.number().int().min(0).max(9) }),
  z.object({ type: z.literal('perk'), index: z.number().int().min(0).max(4) }),
  z.object({ type: z.literal('descend') }),
])
export type DungeonIntent = z.infer<typeof dungeonIntentSchema>

export const MAX_BATCH_INTENTS = 8
export const actBodySchema = z.object({
  /** Client-minted; a retried batch with the same id is answered from the stored result. */
  batchId: z.string().min(8).max(64),
  fromTurn: z.number().int().min(0),
  intents: z.array(dungeonIntentSchema).min(1).max(MAX_BATCH_INTENTS),
})
export type ActBody = z.infer<typeof actBodySchema>

export const startRunBodySchema = z.object({
  kind: z.enum(['daily', 'practice']),
  cls: z.enum(CLASS_IDS as [ClassId, ...ClassId[]]),
})
export type StartRunBody = z.infer<typeof startRunBodySchema>

// ---- what the client sees ------------------------------------------------------------------------

export interface HeroView {
  pos: Pos
  cls: ClassId
  hp: number
  maxHp: number
  atk: number
  def: number
  level: number
  xp: number
  nextXp: number
  fov: number
  perks: PerkId[]
  inventory: ItemId[]
  secondWind: boolean
}

export interface MonsterView {
  id: number
  kind: MonsterId
  pos: Pos
  hp: number
  maxHp: number
  frozen: number
  boss: boolean
}

export interface ItemView {
  pos: Pos
  item: ItemId | 'gold'
  amount: number
}

export interface DungeonView {
  id: string
  kind: DungeonKind
  /** Day key of a daily run (null for practice). */
  day: string | null
  floor: number
  turn: number
  status: RunStatus
  hero: HeroView
  /** `MAP_CELLS` characters; ' ' where the hero has never looked. */
  tiles: string
  /** `MAP_CELLS` of '0'/'1': what is in sight right now. */
  visible: string
  monsters: MonsterView[]
  items: ItemView[]
  pendingPerks: PerkId[]
  kills: number
  gold: number
  score: number
  bossAlive: boolean
  log: string[]
}

export type DungeonEvent =
  | { type: 'heroMoved'; from: Pos; to: Pos }
  | { type: 'heroAttacked'; id: number; dmg: number; crit: boolean; killed: boolean }
  | { type: 'monsterHurt'; id: number; dmg: number; killed: boolean }
  | { type: 'heroHurt'; dmg: number; by: number | null }
  | { type: 'monsterAttacked'; id: number; dmg: number }
  | { type: 'monsterShot'; id: number; from: Pos | null; dmg: number }
  | { type: 'monsterMoved'; id: number; from: Pos; to: Pos }
  | { type: 'monsterSpawned'; id: number; kind: MonsterId; pos: Pos }
  | { type: 'monsterBlinked'; id: number; from: Pos; to: Pos }
  | { type: 'monsterDied'; id: number; pos: Pos; xp: number }
  | { type: 'monsterSplit'; id: number; childId: number; pos: Pos }
  | { type: 'frozen'; ids: number[] }
  | { type: 'pickedUp'; item: ItemId; pos: Pos; applied: boolean }
  | { type: 'gold'; amount: number; pos: Pos }
  | { type: 'chestOpened'; pos: Pos; items: ItemId[] }
  | { type: 'inventoryFull'; pos: Pos }
  | { type: 'used'; item: ItemId; targets: number[] }
  | { type: 'teleported'; from: Pos; to: Pos }
  | { type: 'healed'; amount: number }
  | { type: 'levelUp'; level: number }
  | { type: 'perkPicked'; perk: PerkId }
  | { type: 'descended'; floor: number }
  | { type: 'stairsRevealed'; pos: Pos }
  | { type: 'secondWind' }
  | { type: 'died'; by: string | null }
  | { type: 'won' }

// ---- HTTP shapes -----------------------------------------------------------------------------

export interface RunSummary {
  id: string
  kind: DungeonKind
  day: string | null
  cls: ClassId
  status: RunStatus
  floor: number
  score: number
  kills: number
  gold: number
  turn: number
  startedAt: string
  endedAt: string | null
}

export interface DailyEntry {
  rank: number
  player: PlayerPublic & { color: Color }
  cls: ClassId
  status: RunStatus
  floor: number
  score: number
  kills: number
  turn: number
  endedAt: string | null
}

export interface RunEnd {
  status: RunStatus
  score: number
  /** Position on the day's board (daily runs only). */
  rank: number | null
  xpGained: number
  levelBefore: number
  levelAfter: number
}

export interface ActResponse {
  view: DungeonView
  events: DungeonEvent[]
  /** How many intents of the batch were applied. */
  applied: number
  rejected?: { index: number; code: string }
  end?: RunEnd
}

export interface DungeonToday {
  day: string
  /** Server time of the next reset and the server's clock, for a countdown. */
  resetAt: number
  serverNow: number
  run: RunSummary | null
  /** An unfinished daily run from a previous day, still resumable and scoreable for that day. */
  carryOver: RunSummary | null
  practice: RunSummary | null
  unlockedClasses: ClassId[]
  board: DailyEntry[]
  me: DailyEntry | null
}

export const DUNGEON_ERROR_CODES = [
  'RUN_OVER',
  'PERK_PENDING',
  'BLOCKED',
  'NOT_ON_STAIRS',
  'EMPTY_SLOT',
  'NO_TARGET',
  'BAD_PERK',
  'STALE_TURN',
  'CLASS_LOCKED',
  'RUN_NOT_FOUND',
] as const
export type DungeonErrorCode = ErrorCode | (typeof DUNGEON_ERROR_CODES)[number]

export const DUNGEON_ERROR_TEXT: Record<(typeof DUNGEON_ERROR_CODES)[number], string> = {
  RUN_OVER: 'This run is over',
  PERK_PENDING: 'Pick a perk first',
  BLOCKED: 'Something is in the way',
  NOT_ON_STAIRS: 'You are not on the stairs',
  EMPTY_SLOT: 'That pocket is empty',
  NO_TARGET: 'Nothing in sight to hit',
  BAD_PERK: 'That is not one of the choices',
  STALE_TURN: 'Your view was out of date; it has been refreshed',
  CLASS_LOCKED: 'That class is still locked',
  RUN_NOT_FOUND: 'That run does not exist',
}
